import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { Assessment } from '@/features/assessments/types'
import { useAssessmentProgress, useSaveAnswer, useUpdateAssessment, useServeFile } from '@/features/assessments/hooks'
import { assessmentsApi } from '@/features/assessments/api'
import { useEngineQuestions } from '@/features/questions/hooks'
import { useToast } from '@/hooks/use-toast'
import { CheckCircle2 } from 'lucide-react'
import { QuestionCarousel } from '@/features/assessments/components/question-carousel'

interface AnswerOption {
  value: number
  label: string
  description?: string
}

interface Question {
  id: string
  domain_id: string
  domain_name?: string
  category: string
  text: string
  description?: string
  maturity_level?: number
  answer_options?: AnswerOption[]
}

interface Domain {
  id: string
  name: string
  description?: string
  questions: Question[]
}

interface QuestionnaireFormProps {
  assessmentId: string
  assessment?: Assessment
  onComplete?: () => void
  readOnly?: boolean
}

export function QuestionnaireForm({ assessmentId, assessment, onComplete, readOnly = false }: QuestionnaireFormProps) {
  const [answers, setAnswers] = useState<Record<string, number>>({})
  const [comments, setComments] = useState<Record<string, string>>({})
  const [attachments, setAttachments] = useState<Record<string, File | null>>({})
  const [existingAttachments, setExistingAttachments] = useState<Record<string, string>>({})
  const [isSaving, setIsSaving] = useState(false)
  const [openDomain, setOpenDomain] = useState<string | undefined>(undefined)
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false)
  const [loadedDomains, setLoadedDomains] = useState<Set<string>>(new Set())
  const { toast } = useToast()

  // Load questions via hook
  const { data: questionsRaw, isLoading: questionsLoading } = useEngineQuestions({ detailed: true, per_page: 1000 })
  const saveAnswerMutation = useSaveAnswer()
  const updateAssessmentMutation = useUpdateAssessment()
  const serveFileMutation = useServeFile()

  // Included domains filter from assessment config
  const includedDomainIds = assessment?.included_domain_ids
  const includedSet = includedDomainIds?.length ? new Set(includedDomainIds) : null

  // Resolve scope-based max level for question filtering
  const assessmentScope = assessment?.assessment_scope || 'complete'
  const scopeLevel = assessment?.scope_level

  // Group questions by domain, applying scope-based level filtering
  const domains = useMemo(() => {
    const raw = questionsRaw?.data || questionsRaw || {}
    const questionsData = raw.items || raw.data || (Array.isArray(raw) ? raw : [])
    if (!Array.isArray(questionsData)) return [] as Domain[]

    const domainMap = new Map<string, Domain>()

    questionsData.forEach((q: any) => {
      const domainId = q.domain_id || q.domain

      // Skip domains not included in the assessment configuration
      if (includedSet && !includedSet.has(domainId)) return

      // Apply scope-based level filtering
      const questionLevel = q.maturity_level ?? 1
      if (assessmentScope === 'level' && scopeLevel) {
        // Only include questions up to the scope level
        if (questionLevel > scopeLevel) return
      }
      // Note: 'industry' scope filtering happens server-side via progress API,
      // but we also filter client-side for immediate UX. For industry scope,
      // the backend results/scoring handle the per-domain level caps.
      // 'complete' scope includes all questions.

      if (!domainMap.has(domainId)) {
        domainMap.set(domainId, {
          id: domainId,
          name: q.domain_name || q.domain || domainId,
          description: '',
          questions: []
        })
      }
      const domain = domainMap.get(domainId)!

      // Convert answer_options if it's a string (JSON)
      let answerOptions = q.answer_options
      if (typeof answerOptions === 'string') {
        try {
          answerOptions = JSON.parse(answerOptions)
        } catch {
          answerOptions = []
        }
      }

      domain.questions.push({
        id: q.id,
        domain_id: domainId,
        domain_name: q.domain_name,
        category: q.category || '',
        text: q.text || '',
        description: q.description,
        maturity_level: q.maturity_level,
        answer_options: answerOptions || []
      })
    })

    return Array.from(domainMap.values())
  }, [questionsRaw, includedSet, assessmentScope, scopeLevel])

  const isLoading = questionsLoading

  // Use backend progress API for accurate stats
  const { data: progressData } = useAssessmentProgress(assessmentId)
  const totalQuestions = progressData?.total_questions ?? domains.reduce((sum, d) => sum + d.questions.length, 0)
  const answeredQuestions = progressData?.total_answered ?? Object.keys(answers).length
  const progress = progressData?.overall_completion ?? (totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0)
  const backendDomains: Record<string, { answered_questions: number; total_questions: number; average_score: number; completion_percentage: number }> = {}
  if (progressData?.domains) {
    for (const d of progressData.domains) {
      backendDomains[d.domain_id] = d
    }
  }

  // Fetch answers for a domain when it's expanded
  const handleDomainExpand = async (domainId: string | undefined) => {
    if (!domainId) {
      setOpenDomain(undefined) // collapsed
      return
    }

    // If already loaded, just open
    if (loadedDomains.has(domainId)) {
      setOpenDomain(domainId)
      return
    }

    const domain = domains.find(d => d.id === domainId)
    if (!domain) {
      setOpenDomain(domainId)
      return
    }

    try {
      const questionIds = domain.questions.map(q => q.id)
      const res = await assessmentsApi.batchAnswers(assessmentId, questionIds)
      const rawAnswers = res.data?.items || []

      const newAnswers: Record<string, number> = {}
      const newComments: Record<string, string> = {}
      const newAttachments: Record<string, string> = {}

      rawAnswers.forEach((a: any) => {
        newAnswers[a.question_id] = a.value ?? a.score
        if (a.comment || a.answer_text) {
          newComments[a.question_id] = a.comment || a.answer_text
        }
        if (a.attachment_name) {
          newAttachments[a.question_id] = a.attachment_name
        }
      })

      setAnswers(prev => ({ ...prev, ...newAnswers }))
      setComments(prev => ({ ...prev, ...newComments }))
      setExistingAttachments(prev => ({ ...prev, ...newAttachments }))
      setLoadedDomains(prev => new Set(prev).add(domainId))
    } catch (err) {
      console.error('Failed to load domain answers:', err)
    }

    // Open accordion AFTER answers are loaded so carousel starts at first unanswered
    setOpenDomain(domainId)
  }

  // Auto-expand the first domain with unanswered questions (using backend progress data)
  useEffect(() => {
    if (hasAutoExpanded || domains.length === 0) return
    // Wait until progress data is available
    if (!progressData) return

    // Find first incomplete domain using backend stats
    const firstIncompleteDomain = domains.find(d => {
      const bd = backendDomains[d.id]
      return !bd || bd.answered_questions < bd.total_questions
    })

    if (firstIncompleteDomain) {
      handleDomainExpand(firstIncompleteDomain.id)
    } else if (domains.length > 0) {
      handleDomainExpand(domains[0].id)
    }
    setHasAutoExpanded(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domains, progressData, hasAutoExpanded])

  const saveAnswer = async (questionId: string, value: number, comment?: string, file?: File) => {
    setIsSaving(true)
    try {
      await saveAnswerMutation.mutateAsync({
        assessmentId,
        data: {
          questionId,
          value,
          comment,
          timestamp: new Date().toISOString()
        },
        file
      })
    } catch (error) {
      console.error('Failed to save answer:', error)
      toast({
        title: "Error",
        description: "Failed to save answer. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleAnswerChange = async (questionId: string, value: number) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }))

    const comment = comments[questionId]
    const file = attachments[questionId]
    await saveAnswer(questionId, value, comment, file || undefined)
  }

  const handleCommentChange = async (questionId: string, comment: string) => {
    setComments(prev => ({ ...prev, [questionId]: comment }))

    const value = answers[questionId]
    const file = attachments[questionId]
    if (value !== undefined) {
      await saveAnswer(questionId, value, comment, file || undefined)
    }
  }

  const handleFileChange = async (questionId: string, file: File | null) => {
    setAttachments(prev => ({ ...prev, [questionId]: file }))

    const value = answers[questionId]
    const comment = comments[questionId]
    if (value !== undefined) {
      await saveAnswer(questionId, value, comment, file || undefined)
    }
  }

  const handleDownloadFile = async (questionId: string) => {
    try {
      const filename = existingAttachments[questionId] || 'attachment'
      const response = await serveFileMutation.mutateAsync({ assessmentId, questionId })
      const blob = new Blob([response.data])
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Failed to download file:', error)
      toast({
        title: "Error",
        description: "Failed to download file. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleComplete = async () => {
    try {
      await updateAssessmentMutation.mutateAsync({ id: assessmentId, data: { status: 'completed' } })
      toast({
        title: "Success",
        description: "Assessment completed successfully!",
      })
      onComplete?.()
    } catch (error) {
      console.error('Failed to complete assessment:', error)
      toast({
        title: "Error",
        description: "Failed to complete assessment. Please try again.",
        variant: "destructive",
      })
    }
  }

  // Use backend progress data for complete button — not local answers
  const isAllComplete = progressData
    ? progressData.overall_completion === 100
    : false

  // Domain-level stats — prefer backend, fallback to local
  const getDomainStats = (domain: Domain) => {
    const bd = backendDomains[domain.id]
    if (bd) {
      return {
        answered: bd.answered_questions,
        total: bd.total_questions,
        avgScore: bd.average_score,
        completionPct: bd.completion_percentage,
        isComplete: bd.answered_questions === bd.total_questions && bd.total_questions > 0,
      }
    }
    // Fallback to local
    const answered = domain.questions.filter(q => answers[q.id] !== undefined).length
    const total = domain.questions.length
    return {
      answered,
      total,
      avgScore: 0,
      completionPct: total > 0 ? Math.round((answered / total) * 100) : 0,
      isComplete: answered === total && total > 0,
    }
  }

  if (isLoading && domains.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-lg font-medium">Loading questions...</div>
          <div className="text-sm text-muted-foreground mt-2">Please wait</div>
        </div>
      </div>
    )
  }

  if (domains.length === 0) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900">No questions available</h2>
        <p className="text-gray-500 mt-2">Please ensure questions are imported into the database.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Progress Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>{readOnly ? 'Assessment Answers (Read Only)' : 'IT Maturity Assessment'}</CardTitle>
              <CardDescription>
                {domains.length} domains · {totalQuestions} total questions
              </CardDescription>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">
                {answeredQuestions} of {totalQuestions} questions answered
              </div>
              <Progress value={progress} className="w-32 mt-2" />
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Single-Expand Accordion with Carousel */}
      <Accordion
        type="single"
        collapsible
        value={openDomain}
        onValueChange={handleDomainExpand}
        className="space-y-3"
      >
        {domains.map((domain) => {
          const stats = getDomainStats(domain)

          return (
            <AccordionItem key={domain.id} value={domain.id} className="border rounded-lg bg-white shadow-sm px-4">
              <AccordionTrigger className="hover:no-underline py-5">
                <div className="flex items-center gap-4 w-full mr-4">
                  {/* Completion icon */}
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${stats.isComplete
                    ? 'bg-green-100 text-green-600'
                    : stats.answered > 0
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-400'
                    }`}>
                    {stats.isComplete ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <span className="text-sm font-bold">{stats.answered}</span>
                    )}
                  </div>

                  {/* Domain info */}
                  <div className="flex-1 text-left min-w-0">
                    <div className="font-semibold text-base">{domain.name}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">
                      {stats.answered}/{stats.total} answered
                      {stats.answered > 0 && ` · Avg: ${stats.avgScore.toFixed(1)} · ${stats.completionPct}%`}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-24 shrink-0">
                    <Progress value={(stats.answered / stats.total) * 100} className="h-2" />
                  </div>
                </div>
              </AccordionTrigger>

              <AccordionContent>
                <div className="py-4">
                  <QuestionCarousel
                    questions={domain.questions}
                    answers={answers}
                    comments={comments}
                    attachments={attachments}
                    existingAttachments={existingAttachments}
                    onAnswerChange={handleAnswerChange}
                    onCommentChange={handleCommentChange}
                    onFileChange={handleFileChange}
                    onDownloadFile={handleDownloadFile}
                    readOnly={readOnly}
                    isSaving={isSaving}
                    autoAdvance={!readOnly}
                    autoAdvanceDelay={600}
                    startAtFirstUnanswered
                    assessmentId={assessmentId}
                    onNextDomain={() => {
                      const idx = domains.findIndex(d => d.id === domain.id)
                      if (idx < domains.length - 1) {
                        handleDomainExpand(domains[idx + 1].id)
                      }
                    }}
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>

      {/* Complete Assessment - only shown in edit mode */}
      {!readOnly && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {isSaving && 'Saving...'}
              </div>
              <Button
                onClick={handleComplete}
                disabled={!isAllComplete || isLoading}
                size="lg"
              >
                {isLoading ? 'Completing...' : 'Complete Assessment'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
